import { useEffect, useState } from "react";
import { IPageLink, ITableLookup } from "../../../base/models";
import moment from "moment";
import { IShuttleBookings } from "./properties/shuttleBooking";
import { ProgressIndicator } from "../../../widgets";
import { useGetLookupLabel } from "../typography/name-lookup/useGetLookupLabel";
import { useGetPayment } from "./properties/useGetPayment";
import { HdfcButton } from "../pg-integration";
import { useGetCapacity } from "./properties/useGetCapacity";
import { useAuth } from "../../../../modules/auth";
import { DownloadLinkCell } from "../downloadable-document";

export const BookingsDetails = ({ item,
  paymentDoneToggle,
  tableLookup,
  handleChangeStatus,
  enableTimer,
  loading,
  successPage,
  failedPage,
  enablePayment,
  enableDownload
}: {
  item: IShuttleBookings,
  paymentDoneToggle?: boolean,
  enableTimer: any,
  successPage?: IPageLink,
  failedPage?: IPageLink,
  enablePayment?: boolean,
  tableLookup: ITableLookup,
  handleChangeStatus: (bookingId: string) => void,
  loading: string | null,
  enableDownload?: boolean
}) => {
  const { getLookupLabel } = useGetLookupLabel();
  const [name, setName] = useState<string>();
  const value = item.routeId;
  const { paymentId } = useGetPayment(item.id || '');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCancelDisabled, setIsCancelDisabled] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<string | null>(null);
  const [timerExpired, setTimerExpired] = useState(false);
  const [countdown, setCountdown] = useState<string>("");
  const { capacities } = useGetCapacity();


  useEffect(() => {
    getLookupLabel({ tableLookup, value })
      .then(name => setName(name));
    // eslint-disable-next-line
  }, [tableLookup, value]);

  const extractSourceUrl = (url: string): string | null => {
    try {
      const urlObj = new URL(url);
      return urlObj.searchParams.get("sourceurl");
    } catch (error) {
      return null;
    }
  };

  const sourceUrl = extractSourceUrl(item.pdfURL);

  useEffect(() => {
    if (!enableTimer) return;

    const interval = setInterval(() => {
      if (item) {
        const createdOnTime = moment(item.createdon, "MM/DD/YYYY HH:mm:ss").add(5.5, "hours").subtract(1, "minutes");
        const currentTime = moment();
        const duration = moment.duration(moment(createdOnTime).add(10, "minutes").add(55, "seconds").diff(currentTime));

        if (duration.asMilliseconds() <= 0) {
          setTimerExpired(true);
          setCountdown("Time Expired");
          clearInterval(interval);
        } else {
          const minutes = Math.floor(duration.asMinutes());
          const seconds = duration.seconds();
          setCountdown(`${minutes}m ${seconds}s`);
        }
      }
    }, 1000);

    return () => clearInterval(interval);

    // eslint-disable-next-line
  }, [item]);

  useEffect(() => {
    const bookingTime = moment(item.bookingDateandTime, "MM/DD/YYYY HH:mm:ss").add(5.5, 'hours');
    const currentTime = moment();
    const timeDiff = bookingTime.diff(currentTime, 'minutes');

    if (capacities) {
      const cancelThresholdMinutes =
        (capacities.shuttlehours ? capacities.shuttlehours * 60 : 0) +
        (capacities.shuttleminutes ? capacities.shuttleminutes : 0);

      if (timeDiff < cancelThresholdMinutes) {
        setIsCancelDisabled(true);

        let messageParts: string[] = [];
        if (capacities.shuttlehours) {
          messageParts.push(`${capacities.shuttlehours} hour${capacities.shuttlehours > 1 ? "s" : ""}`);
        }
        if (capacities.shuttleminutes) {
          messageParts.push(`${capacities.shuttleminutes} minute${capacities.shuttleminutes > 1 ? "s" : ""}`);
        }

        setErrorMessage(
          `Booking cannot be cancelled before ${messageParts.join(" and ")} of departure`
        );
      } else {
        setIsCancelDisabled(false);
        setErrorMessage(null);
      }
    }
  }, [item.bookingDateandTime, capacities]);

  const bookingStatus = (status: number) => {
    switch (status) {
      case 365970000: return "Draft";
      case 100000001: return "Payment Failed";
      case 100000006: return "Scheduled";
      case 100000000: return "Completed";
      case 100000004: return "Cancelled";
      default: return "Not Confirmed";
    }
  };

  const statusBadgeClasses = (status: number) => {
    switch (status) {
      case 100000006: // Scheduled
        return ["badge", "bg-primary", "text-white", "text-center"];
      case 100000000: // Completed
        return ["badge", "bg-success", "text-white", "text-center"];
      case 100000004: // Cancelled
        return ["badge", "bg-danger", "text-white", "text-center"];
      case 365970000: // Payment Due
        return ["badge", "bg-warning", "text-white", "text-center"];
      default: //Not Confirmed
        return ["badge", "bg-danger", "text-white", "text-center"];
    }
  };

  return (
    <>
      {enableTimer && timerExpired ? (
        <div className="alert alert-danger">
          This booking is not available. Kindly create a new one.
        </div>
      ) : (
        <>
          {paymentDoneToggle ? (item.paymentdone && (<>
            <div className="card p-3 mb-3 border border-dark rounded wrap">
              <div className="row g-3">
                <div className="col-6 col-lg mb-1 me-lg-5">
                  <strong>Booking ID</strong><br />
                  <div className="text-primary">{item.bookingID}</div>
                </div>

                <div className="col-6 col-lg-2 mb-1">
                  <strong>Booking Date & Time</strong><br />
                  {moment(item.bookingDateandTime, "MM/DD/YYYY HH:mm:ss")
                    .add(5.5, 'hours')
                    .format("DD MMM YYYY HH:mm")}
                </div>

                {!enablePayment && <div className="col-6 col-lg-2 mb-1">
                  <strong>Transaction Number</strong><br />
                  {paymentId?.paymentId}
                </div>}

                <div className="col-6 col-lg mb-1 text-lg-center">
                  <strong>Amount</strong><br />
                  ₹{item.totalCost}
                </div>

                <div className="col-6 col-lg-2 mb-1 text-lg-center">
                  <strong>Route</strong><br />
                  <span className="d-block text-wrap">{name}</span>
                </div>

                {!enablePayment && <div className="col-6 col-lg mb-1 text-lg-center">
                  <strong>Passengers</strong><br />
                  {item.capacity}
                </div>}

                <div className="col-4 col-lg mb-1 text-lg-center">
                  <strong>Status</strong><br />
                  <div className="d-inline-block">
                    <div className={`me-2 mt-1 rounded-pill ${statusBadgeClasses(item.bookingStatus).join(' ')}`}>
                      {bookingStatus(item.bookingStatus)}
                    </div>
                  </div>
                </div>

                {((item.bookingStatus === 100000006 || item.bookingStatus === 100000000) && !enablePayment) && (
                  <>
                    {item.bookingStatus !== 100000000 && (
                      <div className="col-4 col-lg mb-1">
                        <strong></strong><br />
                        <div className="d-inline-block">
                          <button
                            className={`btn btn-sm ${isCancelDisabled || loading === item.bookingID ? 'btn-secondary' : 'btn-warning'}`}
                            style={{
                              backgroundColor: isCancelDisabled ? 'grey' : '#ffc107',
                              color: 'white',
                              borderColor: 'transparent',
                            }}
                            disabled={isCancelDisabled || loading === item.bookingID}
                            onMouseEnter={(e) => {
                              if (!isCancelDisabled) {
                                e.currentTarget.style.backgroundColor = '#ffc107';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isCancelDisabled) {
                                e.currentTarget.style.backgroundColor = '#ffc107';
                              }
                            }}
                            onClick={() => {
                              if (!isCancelDisabled) {
                                setSelectedBooking(item.id || '');
                                setShowConfirm(true);
                              }
                            }}
                          >
                            {loading === item.id ? (
                              <ProgressIndicator show={true} message='Please wait...' />
                            ) : (
                              <>
                                <i className="bi bi-x-circle"></i> Cancel
                              </>
                            )}
                          </button>
                        </div>
                        {showConfirm && (
                          <div className="modal" style={{ display: 'block', background: 'rgba(0,0,0,0.5)', position: 'fixed', top: '0', left: '0', width: '100vw', height: '100vh', zIndex: '1050' }}>
                            <div className="modal-dialog modal-dialog-centered" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', transform: 'translateY(-50px)' }}>
                              <div className="modal-content">
                                <div className="modal-header">
                                  <h5 className="modal-title text-center">Confirm Cancellation</h5>
                                  <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowConfirm(false)}
                                  ></button>
                                </div>
                                <div className="modal-body text-center">
                                  <p>Are you sure you want to cancel this Booking?</p>
                                </div>
                                <div className="modal-footer">
                                  <button
                                    className="btn btn-danger"
                                    onClick={() => {
                                      setShowConfirm(false);
                                      if (selectedBooking) {
                                        handleChangeStatus(selectedBooking);
                                      }
                                    }}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    className="btn btn-secondary"
                                    onClick={() => setShowConfirm(false)}
                                  >
                                    No
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="col-4 col-lg mb-1">
                      <strong></strong><br />
                      <div className="d-inline-block">
                        <a href={item.pdfURL} target="_blank" className="btn btn-primary btn-sm" rel="noreferrer">
                          <i className="bi bi-download"></i> Receipt
                        </a>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {(errorMessage && item.bookingStatus === 100000006) &&
                <div className="text-danger mt-1">
                  <p>{errorMessage}</p>
                </div>
              }
            </div>  </>)) : (<>
              <div className="card p-3 mb-3 border border-dark rounded wrap">
                <div className="row g-3">
                  <div className="col-6 col-lg mb-1 me-lg-5">
                    <strong>Booking ID</strong><br />
                    <div className="text-primary">{item.bookingID}</div>
                  </div>

                  <div className="col-6 col-lg-2 mb-1">
                    <strong>Booking Date & Time</strong><br />
                    {moment(item.bookingDateandTime, "MM/DD/YYYY HH:mm:ss")
                      .add(5.5, 'hours')
                      .format("DD MMM YYYY HH:mm")}
                  </div>

                  {!enablePayment && <div className="col-6 col-lg-2 mb-1">
                    <strong>Transaction Number</strong><br />
                    {paymentId?.paymentId}
                  </div>}

                  <div className="col-6 col-lg mb-1 text-lg-center">
                    <strong>Amount</strong><br />
                    ₹{item.totalCost}
                  </div>

                  <div className="col-6 col-lg-2 mb-1 text-lg-center">
                    <strong>Route</strong><br />
                    <span className="d-block text-wrap">{name}</span>
                  </div>

                  {!enablePayment && <div className="col-6 col-lg mb-1 text-lg-center">
                    <strong>Passengers</strong><br />
                    {item.capacity}
                  </div>}

                  <div className="col-4 col-lg mb-1 text-lg-center">
                    <strong>Status</strong><br />
                    <div className="d-inline-block">
                      <div className={`me-2 mt-1 rounded-pill ${statusBadgeClasses(item.bookingStatus).join(' ')}`}>
                        {bookingStatus(item.bookingStatus)}
                      </div>
                    </div>
                  </div>

                  {((item.bookingStatus === 100000006 || item.bookingStatus === 100000000) && !enablePayment) && (
                    <>
                      {item.bookingStatus !== 100000000 && (
                        <div className="col-4 col-lg mb-1">
                          <strong></strong><br />
                          <div className="d-inline-block">
                            <button
                              className={`btn btn-sm ${isCancelDisabled || loading === item.bookingID ? 'btn-secondary' : 'btn-warning'}`}
                              style={{
                                backgroundColor: isCancelDisabled ? 'grey' : '#ffc107',
                                color: 'white',
                                borderColor: 'transparent',
                              }}
                              disabled={isCancelDisabled || loading === item.bookingID}
                              onMouseEnter={(e) => {
                                if (!isCancelDisabled) {
                                  e.currentTarget.style.backgroundColor = '#ffc107';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isCancelDisabled) {
                                  e.currentTarget.style.backgroundColor = '#ffc107';
                                }
                              }}
                              onClick={() => {
                                if (!isCancelDisabled) {
                                  setSelectedBooking(item.id || '');
                                  setShowConfirm(true);
                                }
                              }}
                            >
                              {loading === item.id ? (
                                <ProgressIndicator show={true} message='Please wait...' />
                              ) : (
                                <>
                                  <i className="bi bi-x-circle"></i> Cancel
                                </>
                              )}
                            </button>
                          </div>
                          {showConfirm && (
                            <div className="modal" style={{ display: 'block', background: 'rgba(0,0,0,0.5)', position: 'fixed', top: '0', left: '0', width: '100vw', height: '100vh', zIndex: '1050' }}>
                              <div className="modal-dialog modal-dialog-centered" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', transform: 'translateY(-50px)' }}>
                                <div className="modal-content">
                                  <div className="modal-header">
                                    <h5 className="modal-title text-center">Confirm Cancellation</h5>
                                    <button
                                      type="button"
                                      className="btn-close"
                                      onClick={() => setShowConfirm(false)}
                                    ></button>
                                  </div>
                                  <div className="modal-body text-center">
                                    <p>Are you sure you want to cancel this Booking?</p>
                                  </div>
                                  <div className="modal-footer">
                                    <button
                                      className="btn btn-danger"
                                      onClick={() => {
                                        setShowConfirm(false);
                                        if (selectedBooking) {
                                          handleChangeStatus(selectedBooking);
                                        }
                                      }}
                                    >
                                      Yes
                                    </button>
                                    <button
                                      className="btn btn-secondary"
                                      onClick={() => setShowConfirm(false)}
                                    >
                                      No
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="col-4 col-lg mb-1">
                        <strong></strong><br />
                        <div className="d-inline-block btn btn-primary btn-sm">
                          {/* <a href={item.pdfURL} target="_blank" className="btn btn-primary btn-sm" rel="noreferrer">
                          Receipt
                        </a> */}
                          <i className="bi bi-download"></i>
                          <DownloadLinkCell fieldUrl={sourceUrl || ''}
                            enableDownload={enableDownload}
                            label="Reciept"
                            target="_blank"
                            className="text-white"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {(errorMessage && item.bookingStatus === 100000006) &&
                  <div className="text-danger mt-1">
                    <p>{errorMessage}</p>
                  </div>
                }
              </div></>)
          }
          {enablePayment && <PaymentComponent item={item} successPage={successPage} failedPage={failedPage}
            countdown={countdown} />}
        </>)}
    </>


  );
};

const PaymentComponent = ({ item, successPage, failedPage, countdown }: { item: IShuttleBookings, countdown: string, successPage?: IPageLink, failedPage?: IPageLink }) => {
  const { user } = useAuth();

  if (!successPage || !failedPage) return <></>;

  return (
    <div className="d-flex">
      <div className="d-flex align-items-center text-black fw-bold border border-dark rounded wrap fs-5 px-2 mx-2">
        Pay with
      </div>
      <HdfcButton phoneNumber={user?.phoneNumber} email={user?.email} firstName = {user?.firstName || user?.userName} enrolmentNo={item.bookingID} amount={item.totalCost} currency="INR" failedPage={failedPage} successPage={successPage} entity="edtec365_booking" entityId={item.id} />
      <div className="d-flex align-items-center text-danger fw-bold wrap fs-5 px-2 mx-2">
        {countdown}
      </div>
    </div>
  )
}
